//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from './service';
import ProviderVisitsStatsModel from '../models/provider-visits-stats.model';

/**
 * @service ProviderVisitsStatsService
 * @description Service for retrieving aggregated statistics regarding provider visits.
 * This service is crucial for the dashboard and reporting modules of the Trading Community.
 * It fetches calculated metrics such as total visits, products placed, sales volume, and activity counts.
 *
 * The service interacts with the `/blubay/providervisitstats/` backend endpoint, specifically the `aggregate` function.
 * It supports hierarchical data retrieval, allowing statistics to be viewed from the perspective of:
 * 1. **Business Owner**: Overall performance of the provider business.
 * 2. **Manager**: Performance of their assigned technicians.
 * 3. **Technician**: Individual performance metrics.
 *
 * **Key Responsibilities:**
 * 1. **Data Aggregation**: Fetching summarized data rather than raw logs.
 * 2. **Dashboard Support**: Providing the data source for charts and KPI cards.
 * 3. **Filtering**: Supporting date ranges and specific personnel filters.
 * 4. **Resource Management**: Handling HTTP request lifecycles.
 *
 * @feature 1. Business-level visit statistics.
 * @feature 2. Manager-level team statistics.
 * @feature 3. Technician-level individual statistics.
 * @feature 4. Date-range filtering for trend analysis.
 *
 * @architecture
 * Extends base Service<ProviderVisitsStatsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/providervisitstats/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})

export class ProviderVisitsStatsService extends Service<ProviderVisitsStatsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/providervisitstats/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/providervisitstats/');
  }

  /**
   * @method getVisitsStatsByBusinessId
   * @async
   * @description Fetch aggregated visit statistics for a business, filtered by managers and technicians.
   *
   * This method allows a business view to drill down into specific subsets of their workforce.
   *
   * @param {string} manager_ids - Comma-separated list of manager IDs to filter by.
   * @param {string} technicianIds - Comma-separated list of technician IDs to filter by.
   * @param {string} includeStats - Flag or list of specific statistics to include in the aggregation.
   * @returns {Promise<Array<ProviderVisitsStatsModel>>} Promise resolving to the aggregated statistics.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request to `aggregate`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getVisitsStatsByBusinessId(manager_ids: string, technicianIds: string, includeStats: string): Promise<Array<ProviderVisitsStatsModel>> {
    return new Promise<Array<ProviderVisitsStatsModel>>((resolve, reject) => {
       // Construct query parameters dynamically
      let queryParams = new URLSearchParams({
        manager_id: manager_ids,
        technician_id: technicianIds,
        stats: includeStats
      });

      const request = this.httpClient.get<Array<ProviderVisitsStatsModel>>(`${this.baseUrl}${this.serviceUrl}aggregate?${queryParams.toString()}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getDashboardStatsByBusinessId
   * @async
   * @description Fetch high-level dashboard statistics for a business within a date range.
   *
   * Used to populate the main dashboard KPIs for a business user.
   *
   * @param {string} includeStats - Specifies which stats to calculate (e.g., 'visits,sales').
   * @param {string} [from_date] - Optional start date for the reporting period (YYYY-MM-DD).
   * @param {string} [to_date] - Optional end date for the reporting period (YYYY-MM-DD).
   * @returns {Promise<Array<ProviderVisitsStatsModel>>} Promise resolving to the dashboard stats.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getDashboardStatsByBusinessId(includeStats: string, from_date?: string, to_date?: string): Promise<Array<ProviderVisitsStatsModel>> {
    return new Promise<Array<ProviderVisitsStatsModel>>((resolve, reject) => {
       // Construct query parameters dynamically
      let queryParams = new URLSearchParams({
        stats: includeStats
      });

      // Append from_date and to_date only if they have values
      if (from_date) queryParams.append("from_date", from_date);
      if (to_date) queryParams.append("to_date", to_date);

      const request = this.httpClient.get<Array<ProviderVisitsStatsModel>>(`${this.baseUrl}${this.serviceUrl}aggregate?${queryParams.toString()}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getVisitsStatsByManagerId
   * @async
   * @description Fetch aggregated visit statistics for a manager's team.
   *
   * Allows a manager to see performance metrics for their assigned technicians.
   *
   * @param {string} technicianIds - Comma-separated list of technician IDs to filter by.
   * @param {string} includeStats - Specifies which stats to calculate.
   * @returns {Promise<Array<ProviderVisitsStatsModel>>} Promise resolving to the team stats.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getVisitsStatsByManagerId(technicianIds: string, includeStats: string): Promise<Array<ProviderVisitsStatsModel>> {
    return new Promise<Array<ProviderVisitsStatsModel>>((resolve, reject) => {
      // Construct query parameters dynamically
     let queryParams = new URLSearchParams({
       technician_id: technicianIds,
       stats: includeStats
     });

     const request = this.httpClient.get<Array<ProviderVisitsStatsModel>>(`${this.baseUrl}${this.serviceUrl}aggregate?${queryParams.toString()}`, this.httpOptions).subscribe(response => {
       resolve(response);
     }, (error: HttpErrorResponse) => {
       reject(error);
     });
     this.ongoingRequests.push(request);
   });
  }

  /**
   * @method getDashboardStatsByManagerId
   * @async
   * @description Fetch high-level dashboard statistics for a manager within a date range.
   *
   * Used to populate the main dashboard KPIs for a manager user.
   *
   * @param {string} includeStats - Specifies which stats to calculate.
   * @param {string} [from_date] - Optional start date.
   * @param {string} [to_date] - Optional end date.
   * @returns {Promise<Array<ProviderVisitsStatsModel>>} Promise resolving to the dashboard stats.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getDashboardStatsByManagerId(includeStats: string, from_date?: string, to_date?: string): Promise<Array<ProviderVisitsStatsModel>> {
    return new Promise<Array<ProviderVisitsStatsModel>>((resolve, reject) => {
      // Construct query parameters dynamically
     let queryParams = new URLSearchParams({
      stats: includeStats
     });

     // Append from_date and to_date only if they have values
     if (from_date) queryParams.append("from_date", from_date);
     if (to_date) queryParams.append("to_date", to_date);

     const request = this.httpClient.get<Array<ProviderVisitsStatsModel>>(`${this.baseUrl}${this.serviceUrl}aggregate?${queryParams.toString()}`, this.httpOptions).subscribe(response => {
       resolve(response);
     }, (error: HttpErrorResponse) => {
       reject(error);
     });
     this.ongoingRequests.push(request);
   });
  }

  /**
   * @method getVisitStatsByTechnicianID
   * @async
   * @description Fetch statistics specific to a single technician.
   *
   * Used for individual performance tracking or self-view for technicians.
   *
   * @param {string} technicianId - The unique identifier of the technician.
   * @returns {Promise<Array<ProviderVisitsStatsModel>>} Promise resolving to the technician's stats.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getVisitStatsByTechnicianID(technicianId: string): Promise<Array<ProviderVisitsStatsModel>> {
    return new Promise<Array<ProviderVisitsStatsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProviderVisitsStatsModel>>(`${this.baseUrl}${this.serviceUrl}?technician_id=${technicianId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
